import React from 'react';
import PropTypes from 'prop-types';
import Popup from '../Base/Popup';
import View from '../Designer/View';
import Text from '../Designer/Text';
import Container from '../Designer/Container';
import Editor from '../Designer/Editor';
import ContextHelper from '../Designer/ContextHelper';

export default class OfferLinkEditor extends React.Component {

    constructor(props) {

        super(props);

        this.state = {
            hasCode: false,
            code: null,
        };
    }

    static get propTypes() {

        return {
            context: PropTypes.object.isRequired,
            setRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
        };
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    render() {

        if (!Editor.isEditMode()) {

            if (this.state.hasCode)
                return this.renderCodeEmbedPopup();

            return null;
        }

        return (
            <Popup ref={ (ref) => this.handlePopupRef(ref) } title='Edit link'>
                <View className='flex flex-wrap gap' context={ this.props.context }>
                    { this.renderStorages() }
                </View>
            </Popup>
        );
    }

    renderCodeEmbedPopup() {

        return (
            <Popup
                onShow={ (popup) => this.handleScripts(popup) }
                onClose={ () => this.setState({ hasCode: false }) }
                isActive />
        );
    }

    renderStorages() {

        return this.getAvailableStorages().map(this.renderGroup);
    }

    renderGroup({ name, storageId }) {

        return (
            <React.StrictMode key={ storageId }>

                {/* link */}
                <View className='w-full'>
                    <Text className='text-medium'>Link by { name }:</Text>
                    <Container
                        role='productOfferLink'
                        placeholder={ `Link by ${name}` }
                        storage={ storageId }
                        children={ <Text role='link' placeholder='Link' /> }
                    />
                </View>

                {/* js code */}
                <View className='w-full'>
                    <Text className='text-medium'>JS code by { name }:</Text>
                    <Text
                        role='js_landing'
                        placeholder={ `JS code by ${name}` }
                        storage={ storageId }
                    />
                </View>

            </React.StrictMode>
        );
    }

    //------------------------------------------------------------------------------------------------------------------
    // ACTIONS
    //------------------------------------------------------------------------------------------------------------------

    getAllowedStorages() {

        return [
            { name: 'Country', storageId: 'App\\Entity\\Geo\\Country' },
            { name: 'Template', storageId: 'App\\Entity\\Aggregation\\ProductTemplate' },
            { name: 'Product', storageId: 'App\\Entity\\Aggregation\\Product' },
        ];
    }

    getAvailableStorages() {


        return this
            .getAllowedStorages()
            .map((storage) => {

                const tester = new RegExp(storage.storageId.replaceAll('\\', '\\\\'));

                return this.props.context.storages.find((raw) => tester.test(raw))
                    ? storage
                    : null;
            })
            .filter((storage) => storage != null);
    }

    handlePopupRef(ref) {

        this.popup = ref;
        document.dispatchEvent(new CustomEvent('DOMContentUpdated', { detail: { target: ref } }));
    }

    showEditor() {

        this.popup.show();
    }

    async click() {

        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {

            const
                editor = Editor.getInstance(),
                context = ContextHelper.assign(this.props.context),
                storages = this.getAvailableStorages().reverse();


            for (let i = 0; i < storages.length; i++) {

                const
                    storage = context.getStorage(storages[i].storageId),
                    // eslint-disable-next-line no-await-in-loop
                    code = await editor.get('js_landing', storage.className, storage.id),
                    // eslint-disable-next-line no-await-in-loop
                    link = await editor.get('productOfferLink', storage.className, storage.id);

                // check the code
                if (code && code.trim().length > 0 && code !== 'null') {

                    // eslint-disable-next-line no-await-in-loop
                    await this.runCode(code);
                    resolve(true);

                    return;
                }

                // check the link
                if (link && link.trim().length > 0 && link != '{}' && link !== 'null') {

                    // eslint-disable-next-line no-await-in-loop
                    await this.openLink(link);
                    resolve(true);

                    return;
                }
            }

            resolve(false);
        });
    }

    async runCode(code) {

        this.setState({
            hasCode: true,
            code,
        });
    }

    async handleScripts(popup) {

        if (!popup || !popup?.body)
            return;

        const container = popup.body.querySelector('[data-role="body"]');
        if (container) {

            container.removeAttribute('data-role');

            const
                { addEventListener } = window,
                { code } = this.state,
                template = document.createElement('template');

            window.addEventListener = () => {};
            // intercept any attempt to add an event by an external script

            template.innerHTML = code;

            const { children } = template.content.cloneNode(true);

            let externalScriptWaiting = 0;
            await new Promise((resolve) => {

                Array
                    .from(children)
                    .sort((a, b) => {

                        // move script execution to the end of array

                        if (a.tagName === 'SCRIPT' && b.tagName !== 'SCRIPT')
                            return 1;

                        if (b.tagName === 'SCRIPT' && a.tagName !== 'SCRIPT')
                            return -1;

                        return 0;
                    })
                    .forEach((sourceElement) => {

                        // append elements to DOM

                        const element = document.createElement(sourceElement.tagName);
                        element.innerHTML = sourceElement.innerHTML;
                        Object
                            .values(sourceElement.attributes)
                            .forEach((attribute) => {

                                element.setAttribute(attribute.name, attribute.value);
                            });

                        if (element.tagName == 'SCRIPT')
                            externalScriptWaiting++;

                        container.appendChild(element);
                    });

                if (externalScriptWaiting == 0) {

                    // there were no external scripts in the code

                    resolve();
                }
                else {

                    // better than onload way

                    const externalScriptIntervalId = setInterval(() => {

                        if (externalScriptWaiting == 0) {

                            clearInterval(externalScriptIntervalId);
                            resolve();
                        }

                    }, 100);
                }
            });

            window.addEventListener = addEventListener;
            // cancel interception
        }
    }

    async openLink(link) {

        const linkOptions = JSON.parse(link);
        if (linkOptions?.link)
            window.open(linkOptions.link, '_blank');
            // asynchronous call with _blank doesn't work in safari
    }

}